import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useState, useEffect, useRef } from "react";
import { useLanguage, languages, LanguageCode } from "../components/LanguageContext";
import ContactSection from "./ContactSection";
import Footer from "./Footer";
// import TranslateDropdown from "../components/TranslateDropdown"; // Temporarily commented (Update #10)

export default function MainLayout() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [hasTranslateBar] = useState(false); // setHasTranslateBar removed temporarily (Update #10)
  const { currentLanguage, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're on an article page
  const isArticlePage = location.pathname.startsWith('/insights/');

  // TEMPORARY: Google Translate disabled (Update #10 - 2025-08-20)
  // Original functionality commented out for easy reversal
  useEffect(() => {
    // Google Translate functionality temporarily disabled
    // The translate dropdown will show "Coming Soon" message
  }, [isArticlePage]);
  
  /* ORIGINAL CODE - UNCOMMENT WHEN RE-ENABLING TRANSLATIONS
  // Initialize Google Translate only on article pages
  useEffect(() => {
    if (!isArticlePage) return;
    
    // Add Google Translate script
    const script = document.createElement('script');
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    
    // Define the callback function
    (window as any).googleTranslateElementInit = () => {
      // Initialize for desktop
      if (document.getElementById('google_translate_element')) {
        new (window as any).google.translate.TranslateElement({
          pageLanguage: 'en',
          includedLanguages: 'ja,th,vi',
          layout: (window as any).google.translate.TranslateElement.InlineLayout.SIMPLE,
          autoDisplay: false,
          multilanguagePage: false
        }, 'google_translate_element');
      }
      
      // Initialize for mobile
      if (document.getElementById('google_translate_element_mobile')) {
        new (window as any).google.translate.TranslateElement({
          pageLanguage: 'en',
          includedLanguages: 'ja,th,vi',
          layout: (window as any).google.translate.TranslateElement.InlineLayout.SIMPLE,
          autoDisplay: false,
          multilanguagePage: false
        }, 'google_translate_element_mobile');
      }
    };
    
    document.body.appendChild(script);

    // Monitor for Google Translate bar
    const observer = new MutationObserver(() => {
      const translateBar = document.querySelector('.goog-te-banner-frame');
      setHasTranslateBar(!!translateBar);
    });
    
    observer.observe(document.body, { childList: true, subtree: true });

    // Add a timeout to check if Google Translate loaded
    const checkTimeout = setTimeout(() => {
      if (!(window as any).google?.translate) {
        console.log('Google Translate widget failed to load');
      }
    }, 3000);

    return () => {
      clearTimeout(checkTimeout);
      observer.disconnect();
      // Cleanup
      const scriptElement = document.querySelector('script[src*="translate.google.com"]');
      if (scriptElement) {
        scriptElement.remove();
      }
      delete (window as any).googleTranslateElementInit;
    };
  }, [isArticlePage]);
  */

  const isActivePage = (path: string) => location.pathname === path;

  // Check if current page is a service page
  const isServicePage = () => {
    return location.pathname === '/technology-marketing-solutions' ||
           location.pathname === '/chinese-search-ads-media-outreach' ||
           location.pathname === '/geo-solutions';
  };

  // Check if current page has contact section
  const hasContactSection = !location.pathname.includes('privacy') && 
                           !location.pathname.includes('terms') && 
                           !location.pathname.includes('cookie');

  // Handle Contact Us click
  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false); // Close mobile menu if open
    
    if (hasContactSection) {
      // Scroll to contact on current page
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        const headerOffset = 120; // Account for fixed header height
        const elementPosition = contactSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      // Navigate to home page, then scroll to contact
      navigate('/#contact');
    }
  };

  // Handle hash change for scrolling after navigation
  const prevLocationRef = useRef(location.pathname);
  
  useEffect(() => {
    // Only handle scroll if the pathname actually changed (route navigation)
    const pathChanged = prevLocationRef.current !== location.pathname;
    
    if (location.hash === '#contact') {
      setTimeout(() => {
        const contactSection = document.getElementById('contact');
        if (contactSection) {
          const headerOffset = 120; // Account for fixed header height
          const elementPosition = contactSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else if (pathChanged) {
      // Only scroll to top on actual route change, not on re-renders
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    prevLocationRef.current = location.pathname;
  }, [location]);

  // Define service items for dropdown (GEO Solutions only for Japanese)
  const serviceItems = [
    // GEO Solutions - Japanese only
    ...(currentLanguage === 'ja' ? [{ path: "/geo-solutions", label: "GEOソリューション" }] : []),
    { path: "/technology-marketing-solutions", label: t('nav.socialMediaMarketing') },
    { path: "/chinese-search-ads-media-outreach", label: t('nav.searchAdsMediaOutreach') },
  ];

  // Main navigation items
  const navItems = [
    { path: "/", label: t('nav.home'), isRoute: true },
    { path: null, label: t('nav.services'), isRoute: false, hasDropdown: true },
    { path: "/market-insights", label: t('nav.marketInsights'), isRoute: true },
    { path: "/about-us", label: t('nav.aboutUs'), isRoute: true },
    { path: "#contact", label: t('nav.contactUs'), isRoute: false },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className={`fixed left-0 right-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border transition-all duration-300 ${hasTranslateBar ? 'top-10' : 'top-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20 md:h-24 lg:h-28 w-full">
            {/* Logo - Always visible */}
            <Link 
              to="/" 
              className="flex-shrink-0"
              onClick={(e) => {
                // If already on home page, prevent navigation and scroll to top
                if (location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
            >
              <img 
                src="/images/logo.png" 
                alt={t('accessibility.altZCreative')} 
                className="h-24 md:h-28 lg:h-32 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex flex-1 items-center justify-between px-8 lg:px-12">
              <div className="flex items-center justify-evenly w-full">
                {navItems.map((item) => {
                  // Services dropdown
                  if (item.hasDropdown) {
                    return (
                      <div 
                        key={item.label}
                        className="relative"
                        onMouseEnter={() => setShowServicesDropdown(true)}
                        onMouseLeave={() => setShowServicesDropdown(false)}
                      >
                        <button
                          className={`px-3 lg:px-4 py-4 rounded transition-colors duration-300 cursor-pointer text-sm lg:text-base ${
                            isServicePage()
                              ? 'bg-slate-800/10 text-slate-800'
                              : 'text-foreground hover:text-slate-800'
                          }`}
                        >
                          {item.label}
                        </button>
                        
                        {showServicesDropdown && (
                          <motion.div 
                            className="absolute top-full left-0 bg-white border border-slate-200 rounded-lg shadow-xl min-w-[250px] overflow-hidden"
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            {serviceItems.map((service) => (
                              <Link
                                key={service.path}
                                to={service.path}
                                className={`block px-4 py-3 hover:bg-slate-50 transition-colors ${
                                  isActivePage(service.path) 
                                    ? 'bg-cyan-50 text-cyan-600' 
                                    : 'text-slate-700'
                                }`}
                                onClick={(e) => {
                                  setShowServicesDropdown(false);
                                  // If clicking the current page, prevent default and scroll to top
                                  if (isActivePage(service.path)) {
                                    e.preventDefault();
                                    window.scrollTo({ top: 0, behavior: 'smooth' });
                                  }
                                }}
                              >
                                {service.label}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </div>
                    );
                  }

                  // Regular navigation items
                  if (item.isRoute) {
                    return (
                      <Link
                        key={item.path!}
                        to={item.path!}
                        onClick={(e) => {
                          // If clicking the current page, prevent default and scroll to top
                          if (isActivePage(item.path!)) {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }}
                        className={`px-3 lg:px-4 py-4 rounded transition-colors duration-300 cursor-pointer text-sm lg:text-base ${
                          isActivePage(item.path!)
                            ? 'bg-slate-800/10 text-slate-800'
                            : 'text-foreground hover:text-slate-800'
                        }`}
                      >
                        {item.label}
                      </Link>
                    );
                  }

                  // Contact Us button
                  if (item.path === '#contact') {
                    return (
                      <button
                        key={item.path}
                        onClick={handleContactClick}
                        className="px-3 lg:px-4 py-4 rounded transition-colors duration-300 cursor-pointer text-foreground hover:text-slate-800 text-sm lg:text-base"
                      >
                        {item.label}
                      </button>
                    );
                  }

                  return null;
                })}
              </div>
            </div>

            {/* Language Switcher - Desktop */}
            {/* TEMPORARY: Hide TranslateDropdown on article pages (Update #10) */}
            <div className="hidden md:block flex-shrink-0">
              <LanguageDropdown 
                currentLanguage={currentLanguage} 
                setLanguage={setLanguage} 
              />
              {/* ORIGINAL CODE - UNCOMMENT WHEN RE-ENABLING TRANSLATIONS
              {isArticlePage ? (
                <div className="relative">
                  <TranslateDropdown />
                  <div id="google_translate_element" className="google-translate-container" style={{ display: 'none' }}></div>
                </div>
              ) : (
                <LanguageDropdown 
                  currentLanguage={currentLanguage} 
                  setLanguage={setLanguage} 
                />
              )}
              */}
            </div>

            {/* Mobile only: Language Switcher + Menu Button */}
            {/* TEMPORARY: Hide TranslateDropdown on mobile (Update #10) */}
            <div className="md:hidden flex items-center gap-2 ml-auto">
              {/* Mobile Language Switcher */}
              <div className="scale-90">
                <LanguageDropdown 
                  currentLanguage={currentLanguage} 
                  setLanguage={setLanguage} 
                />
                {/* ORIGINAL CODE - UNCOMMENT WHEN RE-ENABLING TRANSLATIONS
                {isArticlePage ? (
                  <div className="relative">
                    <TranslateDropdown />
                    <div id="google_translate_element_mobile" className="google-translate-container-mobile" style={{ display: 'none' }}></div>
                  </div>
                ) : (
                  <LanguageDropdown 
                    currentLanguage={'en'} 
                    setLanguage={setLanguage} 
                  />
                )}
                */}
              </div>
              
              {/* Hamburger Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-lg text-foreground hover:text-slate-800"
                aria-label={t('accessibility.ariaToggleMenu')}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <motion.div
              className="md:hidden border-t border-border"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
            >
              <div className="py-4 space-y-2">
                {navItems.map((item) => {
                  // Services with submenu in mobile
                  if (item.hasDropdown) {
                    return (
                      <div key={item.label}>
                        <div className="px-4 py-3 text-foreground font-medium">
                          {item.label}
                        </div>
                        <div className="pl-8 space-y-1">
                          {serviceItems.map((service) => (
                            <Link
                              key={service.path}
                              to={service.path}
                              onClick={(e) => {
                                setIsMobileMenuOpen(false);
                                // If clicking the current page, prevent default and scroll to top
                                if (isActivePage(service.path)) {
                                  e.preventDefault();
                                  window.scrollTo({ top: 0, behavior: 'smooth' });
                                }
                              }}
                              className={`block px-4 py-2 rounded-lg transition-colors ${
                                isActivePage(service.path)
                                  ? 'bg-slate-800/10 text-slate-800'
                                  : 'text-foreground hover:text-slate-800'
                              }`}
                            >
                              {service.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  // Regular menu items
                  if (item.isRoute) {
                    return (
                      <Link
                        key={item.path!}
                        to={item.path!}
                        onClick={(e) => {
                          setIsMobileMenuOpen(false);
                          // If clicking the current page, prevent default and scroll to top
                          if (isActivePage(item.path!)) {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }}
                        className={`block px-4 py-3 rounded-lg transition-colors cursor-pointer ${
                          isActivePage(item.path!)
                            ? 'bg-slate-800/10 text-slate-800'
                            : 'text-foreground hover:text-slate-800'
                        }`}
                      >
                        {item.label}
                      </Link>
                    );
                  }

                  // Contact Us button in mobile
                  if (item.path === '#contact') {
                    return (
                      <button
                        key={item.path}
                        onClick={handleContactClick}
                        className="block w-full text-left px-4 py-3 rounded-lg transition-colors cursor-pointer text-foreground hover:text-slate-800 hover:bg-slate-50"
                      >
                        {item.label}
                      </button>
                    );
                  }

                  return null;
                })}
              </div>
            </motion.div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className={`transition-all duration-300 ${hasTranslateBar ? 'pt-32 md:pt-36 lg:pt-40' : 'pt-20 md:pt-24 lg:pt-28'}`}>
        <Outlet />
      </div>

      {/* Contact Section - appears on every page except legal pages and article pages */}
      {!location.pathname.includes('privacy') && 
       !location.pathname.includes('terms') && 
       !location.pathname.includes('cookie') && 
       !location.pathname.includes('/insights/') && (
        <ContactSection />
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}

// Modified Language Dropdown Component (Update #10 modified - 2025-08-28)
// Shows "Coming Soon" for Thai and Vietnamese, allows English and Japanese
// Original functionality commented out for easy reversal
function LanguageDropdown({ currentLanguage, setLanguage }: { 
  currentLanguage: LanguageCode; 
  setLanguage: (lang: LanguageCode) => void; 
}) {
  const { t } = useLanguage();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);

  return (
    <div 
      className="relative"
      onMouseEnter={() => setShowDropdown(true)}
      onMouseLeave={() => setShowDropdown(false)}
    >
      <button 
        className="flex flex-col items-center justify-center px-4 py-2 text-muted-foreground hover:text-slate-800 transition-colors cursor-pointer"
        aria-label={t('accessibility.ariaChangeLanguage')}
        aria-expanded={showDropdown}
      >
        <div className="mb-1 text-xl">{languages[currentLanguage].flag}</div>
        <span className="text-xs">Language</span>
      </button>

      {showDropdown && (
        <div className="absolute top-full right-0 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
          {Object.values(languages).map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                if (lang.code === 'en' || lang.code === 'ja' || lang.code === 'vi') {
                  // English, Japanese, and Vietnamese are enabled
                  setLanguage(lang.code);
                  setShowDropdown(false);
                } else {
                  // Thai shows "Coming Soon"
                  setShowComingSoon(true);
                  setTimeout(() => setShowComingSoon(false), 2000);
                }
              }}
              className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors flex items-center space-x-3 ${
                currentLanguage === lang.code ? 'bg-cyan-50 text-cyan-600' : 'text-slate-700'
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <div>
                <div className="text-sm">{lang.name}</div>
                <div className="text-xs text-slate-500">{lang.nativeName}</div>
              </div>
            </button>
          ))}
          
          {/* Coming Soon notification when language switching is disabled */}
          {showComingSoon && (
            <div className="absolute -bottom-8 left-0 right-0 bg-slate-800 text-white text-xs py-1 px-2 rounded text-center">
              Translation coming soon!
            </div>
          )}
        </div>
      )}
    </div>
  );
  
  /* ORIGINAL CODE - UNCOMMENT WHEN RE-ENABLING TRANSLATIONS
  return (
    <div 
      className="relative"
      onMouseEnter={() => setShowDropdown(true)}
      onMouseLeave={() => setShowDropdown(false)}
    >
      <button 
        className="flex flex-col items-center justify-center px-4 py-2 text-muted-foreground hover:text-slate-800 transition-colors cursor-pointer"
        aria-label={t('accessibility.ariaChangeLanguage')}
        aria-expanded={showDropdown}
      >
        <div className="mb-1 text-xl">{languages[currentLanguage].flag}</div>
        <span className="text-xs">Language</span>
      </button>

      {showDropdown && (
        <div className="absolute top-full right-0 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-50">
          {Object.values(languages).map((lang) => (
            <button
              key={lang.code}
              onClick={() => {
                if (lang.code === 'en' || lang.code === 'ja' || lang.code === 'vi') {
                  // English, Japanese, and Vietnamese are enabled
                  setLanguage(lang.code);
                  setShowDropdown(false);
                } else {
                  // Thai shows "Coming Soon"
                  setShowComingSoon(true);
                  setTimeout(() => setShowComingSoon(false), 2000);
                }
              }}
              className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors flex items-center space-x-3 ${
                currentLanguage === lang.code ? 'bg-cyan-50 text-cyan-600' : 'text-slate-700'
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <div>
                <div className="text-sm">{lang.name}</div>
                <div className="text-xs text-slate-500">{lang.nativeName}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
  */
}